// Speech HTTP routes (C-3), client side: mint and config.
//
// Cookie on the web and bearer in the native shells (`apiUrl` + `authHeaders`).
// Every failure is a `SpeechHttpError{status, code, retryAfterSec, reason}`:
// 401, 402, 415 and 429 are mapped by status because the reused server guards
// emit their own bodies (402: the company's usage credit is used up, as for a
// chat question); `code` decides only for 400, 403 and 503. A network failure
// is status 0, code `request_failed`.
import { apiUrl, authHeaders } from '@/lib/shell';
import {
  SpeechHttpError,
  VOICE_MODE_UNAVAILABLE,
  type SpeechConfig,
  type SpeechHttpErrorCode,
  type SpeechMintRequest,
  type SpeechMintResponse,
  type SpeechUnavailableReason,
  type VoiceModeUnavailableReason,
} from '../contracts';
import { SPEECH_ERROR_CODES } from './protocol';

export const SPEECH_SESSIONS_PATH = '/api/v1/speech/sessions';
export const SPEECH_CONFIG_PATH = '/api/v1/speech/config';

export type SpeechFetch = (input: string, init: RequestInit) => Promise<Response>;
const defaultFetch: SpeechFetch = (input, init) => globalThis.fetch(input, init);

// The WebSocket codes, plus the voice-mode mint's 503 (C2-14), which is HTTP only.
const knownCodes: ReadonlySet<string> = new Set([...SPEECH_ERROR_CODES, VOICE_MODE_UNAVAILABLE]);

const STATUS_CODES: Record<number, SpeechHttpErrorCode> = {
  401: 'not_authenticated',
  402: 'payment_required',
  415: 'json_required',
  429: 'rate_limited',
};
const DEFAULT_BY_STATUS: Record<number, SpeechHttpErrorCode> = {
  400: 'invalid_request',
  403: 'request_origin_not_allowed',
  503: 'speech_unavailable',
};
const REASONS: ReadonlySet<string> = new Set([
  'not_configured',
  'provider_unavailable',
  'policy_off',
  'no_allowlisted_engine',
  'speech_residency_unavailable',
  'processing_policy_unavailable',
]);

export const parseSpeechUnavailableReason = (value: unknown): SpeechHttpError['reason'] =>
  typeof value === 'string' && REASONS.has(value)
    ? (value as SpeechUnavailableReason | VoiceModeUnavailableReason)
    : null;

function bodyCode(body: Record<string, unknown>): string | null {
  const value = body.error ?? body.code;
  return typeof value === 'string' && knownCodes.has(value) ? value : null;
}

const seconds = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const retryAfter = (response: Response, body: Record<string, unknown>): number | null =>
  seconds(response.headers?.get?.('retry-after')) ?? seconds(body.retryAfterSec);

/** The typed error for a non-2xx speech response. */
export async function speechHttpError(response: Response): Promise<SpeechHttpError> {
  const body = ((await response.json().catch(() => null)) ?? {}) as Record<string, unknown>;
  const status = response.status;
  const code =
    STATUS_CODES[status] ??
    (bodyCode(body) as SpeechHttpErrorCode | null) ??
    DEFAULT_BY_STATUS[status] ??
    (status >= 500 ? 'internal' : 'request_failed');
  const reason = parseSpeechUnavailableReason(body.reason);
  return new SpeechHttpError(status, code, retryAfter(response, body), reason);
}

async function send<T>(
  path: string,
  init: RequestInit,
  fetchImpl: SpeechFetch = defaultFetch,
): Promise<T> {
  let response: Response;
  try {
    response = await fetchImpl(apiUrl(path), {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      headers: {
        ...authHeaders(),
        ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
    });
  } catch {
    throw new SpeechHttpError(0, 'request_failed');
  }
  if (!response.ok) throw await speechHttpError(response);
  return (await response.json()) as T;
}

/** POST /api/v1/speech/sessions → 201 with the ticket (C-3). Rejects with SpeechHttpError. */
export const mintSpeechSession = (
  req: SpeechMintRequest,
  fetchImpl?: SpeechFetch,
): Promise<SpeechMintResponse> =>
  send(SPEECH_SESSIONS_PATH, { method: 'POST', body: JSON.stringify(req) }, fetchImpl);

/** GET /api/v1/speech/config: honest availability and the session limits. */
export const fetchSpeechConfig = (fetchImpl?: SpeechFetch): Promise<SpeechConfig> =>
  send(SPEECH_CONFIG_PATH, { method: 'GET' }, fetchImpl);

export const fetchSpeechLanguageHints = (threadId: string): Promise<{ codes: string[] }> =>
  send('/api/v1/speech/language-hints', { method: 'POST', body: JSON.stringify({ threadId }) });
